import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {buildVisualRecipientForEmail} from '../../../../helpers/models/recipientParsers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/reduxHelpers';
import {MessageTypesEnum} from '../../../../models/api/messageModel';
import {RecipientRolesEnum} from '../../../../models/api/recipientModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {makeGetHasComposeChannelExcludingDemo} from '../../../../store/data/channels/channelsSelectors';
import {composeMessageAction} from '../../../../store/data/messages/draftMessagesActions';
import {getIsUserGlobalAdmin} from '../../../../store/data/teammates/teammatesSelectors';
import {navigateToLinkAction} from '../../../../store/stateless/navigationActions';
import {TopBarButton} from '../topBarButton';
import {settingsButtonMargin} from '../topBarHelpers';
import {SettingsHelpWidget} from './settingsHelpWidget';

/*
 * Constants.
 */

const supportEmailRecipient = buildVisualRecipientForEmail(RecipientRolesEnum.TO, 'support@frontapp.com');

/*
 * Styles.
 */

const StyledTopBarButton = styled(TopBarButton)`
  margin-left: ${settingsButtonMargin}px;
`;

/*
 * Component.
 */

export const Help: FC = () => {
  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);
  const hasComposeChannelExcludingDemo = useAppSelector(
    makeGetHasComposeChannelExcludingDemo({
      getSpecifiedMessageType: () => MessageTypesEnum.EMAIL,
    }),
  );

  const dispatch = useAppDispatch();

  const renderHelpButton = (isDropdownOpen: boolean) => (
    <StyledTopBarButton
      iconName="help"
      isSelected={isDropdownOpen}
      renderTooltip={() => <Trans id="help-button">Help and tips</Trans>}
      interactionId="p0-settings-help"
    >
      <Trans id="help-button">Help and tips</Trans>
    </StyledTopBarButton>
  );

  const renderHelpDropdown = (popoverProps: PopoverTransferProps) => {
    const navigateTo = (link: string, interfaceEvent: InterfaceEventsEnum) => () => {
      // Close the dropdown.
      popoverProps.onRequestClose();

      // Track the event.
      trackInterfaceEvent(interfaceEvent);

      // Navigate to the link.
      dispatch(navigateToLinkAction(link));
    };

    const onEmailClick = () => {
      // Close the dropdown.
      popoverProps.onRequestClose();

      // Track the event.
      trackInterfaceEvent(InterfaceEventsEnum.HELP_WIDGET_SUPPORT_CLICK);

      // Open composer.
      dispatch(
        composeMessageAction({
          recipients: [supportEmailRecipient],
          shouldForcePopout: true,
        }),
      );
    };
    return (
      <SettingsHelpWidget
        useDropdownStyle
        isGlobalAdmin={isGlobalAdmin}
        onResourcesClick={navigateTo(
          'https://frontapp.com/resources',
          InterfaceEventsEnum.HELP_WIDGET_RESOURCES_CLICK,
        )}
        onHelpClick={navigateTo(
          'https://help.frontapp.com',
          InterfaceEventsEnum.HELP_WIDGET_HELP_CENTER_CLICK,
        )}
        canEmail={hasComposeChannelExcludingDemo}
        onEmailClick={onEmailClick}
        onOpenClick={onOpenClick}
        {...popoverProps}
      />
    );
  };

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => renderHelpButton(isOpen)}
      renderModal={(popoverProps) => renderHelpDropdown(popoverProps)}
    />
  );
};

/*
 * Helpers.
 */

const onOpenClick = () => {
  trackInterfaceEvent(InterfaceEventsEnum.HELP_WIDGET_OPEN_CLICK);
};
